import React, { createContext, useState, useContext, ReactNode } from "react";
import FormModalComponent from "./index";

interface ModalContextProps {
  showModal: (showModalprops: ModalState) => void;
  hideModal: (id: string) => void;
}

const FormModalContext = createContext<ModalContextProps | undefined>(
  undefined,
);

export const useFormModal = () => {
  const context = useContext(FormModalContext);
  if (!context) {
    throw new Error("useModal must be used within a FormModalProvider");
  }
  return context;
};

interface ModalState {
  id: string;
  open: boolean;
  title: string;
  content: ReactNode;
  okText: string;
  onAddSuccess: (values: unknown) => void;
}

export const FormModalProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [modals, setModals] = useState<ModalState[]>([]);

  const showModal = (showModalprops: ModalState) => {
    setModals((prevModals) => {
      const existingModal = prevModals.find(
        (modal) => modal.id === showModalprops.id,
      );
      if (existingModal) {
        return prevModals.map((modal) =>
          modal.id === showModalprops.id
            ? {
                ...modal,
                open: true,
                title: showModalprops.title,
                content: showModalprops.content,
                okText: showModalprops.okText,
                onAddSuccess: showModalprops.onAddSuccess,
              }
            : modal,
        );
      } else {
        return [
          ...prevModals,
          {
            id: showModalprops.id,
            open: true,
            title: showModalprops.title,
            content: showModalprops.content,
            okText: showModalprops.okText,
            onAddSuccess: showModalprops.onAddSuccess,
          },
        ];
      }
    });
  };

  const hideModal = (id: string) => {
    setModals((prevModals) =>
      prevModals.map((modal) =>
        modal.id === id ? { ...modal, open: false } : modal,
      ),
    );
  };

  return (
    <FormModalContext.Provider value={{ showModal, hideModal }}>
      {children}
      {modals.map((modal) => (
        <FormModalComponent
          key={modal.id}
          open={modal.open}
          title={modal.title}
          content={modal.content}
          okText={modal.okText}
          onClose={() => hideModal(modal.id)}
          onAddSuccess={modal.onAddSuccess}
        />
      ))}
    </FormModalContext.Provider>
  );
};
